import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ASSISTANT_MONTHLY_TOKEN_BUDGET_MAXIMUM } from '@/constants/assistant';
import { OptionChips } from './OptionChips';

interface MonthlyBudgetFieldProps {
  budget: number | null;
  defaultBudget: number;
  onChange: (nextBudget: number | null) => void;
}

const BUDGET_FIELD_ID = 'tenant-assistant-monthly-budget';
const BUDGET_SUMMARY_ID = 'tenant-assistant-monthly-budget-summary';
const MINIMUM_BUDGET_TOKENS = 1;
const WHOLE_NUMBER_PATTERN = /^\d+$/;

function parseBudget(value: string): number | null {
  if (WHOLE_NUMBER_PATTERN.test(value) === false) {
    return null;
  }
  const parsed = Number(value);
  if (parsed < MINIMUM_BUDGET_TOKENS || parsed > ASSISTANT_MONTHLY_TOKEN_BUDGET_MAXIMUM) {
    return null;
  }
  return parsed;
}

export function MonthlyBudgetField({ budget, defaultBudget, onChange }: MonthlyBudgetFieldProps) {
  const { t, i18n } = useTranslation('assistant');
  const [typedBudget, setTypedBudget] = useState(budget === null ? '' : String(budget));
  const formattedDefault = new Intl.NumberFormat(i18n.language).format(defaultBudget);
  const isTypedBudgetValid = parseBudget(typedBudget) !== null;
  return (
    <div className="space-y-4">
      <OptionChips
        label={t('configuration.budget.title')}
        isLabelHidden
        options={[
          { value: true, label: t('configuration.defaultOption', { value: formattedDefault }) },
          { value: false, label: t('configuration.budget.custom') },
        ]}
        selectedValue={budget === null}
        onSelect={function selectBudgetMode(followsDefault) {
          if (followsDefault === true) {
            onChange(null);
            return;
          }
          setTypedBudget(String(defaultBudget));
          onChange(defaultBudget);
        }}
      />
      {budget === null ? null : (
        <div className="space-y-1.5 max-w-xs">
          <Label htmlFor={BUDGET_FIELD_ID}>{t('configuration.budget.label')}</Label>
          <Input
            id={BUDGET_FIELD_ID}
            type="number"
            inputMode="numeric"
            min={MINIMUM_BUDGET_TOKENS}
            max={ASSISTANT_MONTHLY_TOKEN_BUDGET_MAXIMUM}
            step={1}
            value={typedBudget}
            hasError={isTypedBudgetValid === false}
            aria-describedby={BUDGET_SUMMARY_ID}
            onChange={function updateBudget(changeEvent) {
              const nextTypedBudget = changeEvent.target.value;
              setTypedBudget(nextTypedBudget);
              const parsedBudget = parseBudget(nextTypedBudget);
              if (parsedBudget !== null) {
                onChange(parsedBudget);
              }
            }}
          />
          <p id={BUDGET_SUMMARY_ID} className="text-xs text-zoomuted">
            {t('configuration.budget.summary', {
              value: new Intl.NumberFormat(i18n.language).format(budget),
            })}
          </p>
        </div>
      )}
    </div>
  );
}
